import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { GetWithAuth, PutWithAuth } from '../utils/api';
import './Profile.css';

const Profile = () => {
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    province: '',
    country: 'Argentina'
  });
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const { user, token, isAuthenticated, updateUser } = useAuth();

  const fetchUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Si tenemos la info del usuario en el contexto, la usamos
      if (user) {
        setUserInfo({
          name: user.name || '',
          email: user.email || '',
          phone: user.phone || '',
          address: user.address || '',
          city: user.city || '',
          province: user.province || '',
          country: user.country || 'Argentina'
        });
      }

      // También obtenemos datos actualizados del backend si hay endpoint
      try {
        const data = await GetWithAuth('/users/profile');
        if (data.success) {
          setUserInfo({
            name: data.user.name || '',
            email: data.user.email || '',
            phone: data.user.phone || '',
            address: data.user.address || '',
            city: data.user.city || '',
            province: data.user.province || '',
            country: data.user.country || 'Argentina'
          });
        }
      } catch (err) {
        // Si no hay endpoint de perfil, usar solo datos del contexto
        console.log('Using user data from auth context');
      }
    } catch (error) {
      setError('Error al cargar el perfil');
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated() && token) {
      fetchUserProfile();
    } else {
      setLoading(false);
      setError('Debes iniciar sesión para ver tu perfil');
    }
  }, [token, isAuthenticated, fetchUserProfile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setMessage('');
      setError('');

      const data = await PutWithAuth('/users/profile', userInfo);

      if (data.success) {
        setMessage('Perfil actualizado correctamente');
        setEditMode(false);

        // Actualizar contexto de usuario si es posible
        if (updateUser) {
          updateUser(data.user || userInfo);
        }
      } else {
        setError(data.message || 'Error al actualizar el perfil');
      }
    } catch (error) {
      setError('Error al actualizar el perfil');
      console.error('Error updating profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    fetchUserProfile(); // Restaurar datos originales
    setMessage('');
    setError('');
  };

  if (loading) {
    return (
      <div className="container" style={{ padding: '2rem 1rem', minHeight: '70vh' }}>
        <div className="loading">Cargando perfil...</div>
      </div>
    );
  }

  if (error && !isAuthenticated()) {
    return (
      <div className="container" style={{ padding: '2rem 1rem', minHeight: '70vh' }}>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '2rem 1rem', minHeight: '70vh' }}>
      <div className="profile-page">
        <div className="profile-header">
          <h1>Mi Perfil</h1>
          <p>Administra tu información personal</p>
        </div>

        {message && <div className="success-message">{message}</div>}
        {error && <div className="error-message">{error}</div>}

        <div className="profile-content">
          <div className="profile-card">
            <div className="profile-card-header">
              <h2>Información Personal</h2>
              <button
                className={`btn ${editMode ? 'btn-secondary' : 'btn-primary'}`}
                onClick={() => editMode ? handleCancel() : setEditMode(true)}
                disabled={saving}
              >
                {editMode ? 'Cancelar' : 'Editar'}
              </button>
            </div>

            <div className="profile-form">
              <div className="form-group">
                <label htmlFor="name">Nombre completo</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={userInfo.name}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  className="form-control"
                  placeholder="Tu nombre completo"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Correo electrónico</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={userInfo.email}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  className="form-control"
                  placeholder="tu@email.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Teléfono</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={userInfo.phone}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  className="form-control"
                  placeholder="Tu número de teléfono"
                />
              </div>

              <div className="form-group">
                <label htmlFor="address">Dirección</label>
                <textarea
                  id="address"
                  name="address"
                  value={userInfo.address}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  className="form-control"
                  placeholder="Tu dirección completa"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label htmlFor="city">Ciudad *</label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={userInfo.city}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  className="form-control"
                  placeholder="Tu ciudad"
                />
              </div>

              <div className="form-group">
                <label htmlFor="province">Provincia</label>
                <input
                  type="text"
                  id="province"
                  name="province"
                  value={userInfo.province}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  className="form-control"
                  placeholder="Tu provincia"
                />
              </div>

              <div className="form-group">
                <label htmlFor="country">País</label>
                <input
                  type="text"
                  id="country"
                  name="country"
                  value={userInfo.country}
                  disabled
                  className="form-control"
                />
                <small className="form-text">El país está fijado en Argentina</small>
              </div>

              {editMode && (
                <div className="profile-actions">
                  <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Guardando...' : 'Guardar Cambios'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;